import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Settings as SettingsIcon, User, Mail, Shield, Globe, Moon, Sun, Palette, Check, Loader2, Send, Target, Plus, Edit, Trash2, Scale, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage, LANGUAGES } from '@/i18n';
import { TenantBannerCard } from '@/components/TenantBannerCard';
import { useTheme } from '@/contexts/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user } = useOutletContext();
  const [sendgridKey, setSendgridKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('noreply@privaxia.eu');
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [maskedKey, setMaskedKey] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme, toggleTheme } = useTheme();
  
  // Consent Purposes State
  const [purposes, setPurposes] = useState([]);
  const [legalBasisOptions, setLegalBasisOptions] = useState([]);
  const [retentionOptions, setRetentionOptions] = useState([]);
  const [purposeDialog, setPurposeDialog] = useState({ open: false, purpose: null });
  const [purposeForm, setPurposeForm] = useState({
    purpose_id: '',
    name: '',
    description: '',
    legal_basis: 'consent',
    retention_period_days: 365,
    retention_period_label: '1 Ano',
    is_active: true,
    requires_explicit_consent: true,
    order: 0
  });
  const [savingPurpose, setSavingPurpose] = useState(false);

  useEffect(() => {
    if (user?.role === 'operador' || user?.role === 'dpo' || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'tenant_admin') {
      fetchEmailConfig();
      fetchConsentPurposes();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchConsentPurposes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/consent-purposes`, { withCredentials: true });
      setPurposes(response.data.purposes || []);
      setLegalBasisOptions(response.data.legal_basis_options || []);
      setRetentionOptions(response.data.retention_period_options || []);
    } catch (error) {
      console.error('Error fetching consent purposes:', error);
    }
  };

  const handleOpenPurposeDialog = (purpose = null) => {
    if (purpose) {
      setPurposeForm({
        purpose_id: purpose.purpose_id,
        name: purpose.name,
        description: purpose.description || '',
        legal_basis: purpose.legal_basis,
        retention_period_days: purpose.retention_period_days,
        retention_period_label: purpose.retention_period_label || '',
        is_active: purpose.is_active,
        requires_explicit_consent: purpose.requires_explicit_consent,
        order: purpose.order || 0
      });
    } else {
      setPurposeForm({
        purpose_id: '',
        name: '',
        description: '',
        legal_basis: 'consent',
        retention_period_days: 365,
        retention_period_label: '1 Ano',
        is_active: true,
        requires_explicit_consent: true,
        order: purposes.length + 1
      });
    }
    setPurposeDialog({ open: true, purpose });
  };

  const handleSavePurpose = async () => {
    if (!purposeForm.purpose_id || !purposeForm.name) {
      toast.error('ID e Nome são obrigatórios');
      return;
    }

    setSavingPurpose(true);
    try {
      if (purposeDialog.purpose) {
        // Update
        await axios.patch(
          `${BACKEND_URL}/api/consent-purposes/${purposeForm.purpose_id}`,
          purposeForm,
          { withCredentials: true }
        );
        toast.success('Finalidade atualizada com sucesso');
      } else {
        // Create
        await axios.post(
          `${BACKEND_URL}/api/consent-purposes`,
          purposeForm,
          { withCredentials: true }
        );
        toast.success('Finalidade criada com sucesso');
      }
      setPurposeDialog({ open: false, purpose: null });
      fetchConsentPurposes();
    } catch (error) {
      console.error('Error saving purpose:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar finalidade');
    } finally {
      setSavingPurpose(false);
    }
  };

  const handleDeletePurpose = async (purposeId) => {
    if (!confirm('Tem a certeza que deseja eliminar esta finalidade?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/consent-purposes/${purposeId}`, { withCredentials: true });
      toast.success('Finalidade eliminada');
      fetchConsentPurposes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar finalidade');
    }
  };

  const handleRetentionChange = (days) => {
    const option = retentionOptions.find(o => o.value === parseInt(days));
    setPurposeForm(prev => ({
      ...prev,
      retention_period_days: parseInt(days),
      retention_period_label: option?.label || `${days} dias`
    }));
  };

  const getLegalBasisLabel = (value) => {
    const option = legalBasisOptions.find(o => o.value === value);
    return option?.label || value;
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/config/email`, { withCredentials: true });
      setSenderEmail(response.data.sender_email || 'noreply@privaxia.eu');
      setEmailConfigured(response.data.is_configured || false);
      setMaskedKey(response.data.sendgrid_api_key_masked || '');
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/config/email`,
        null,
        { 
          params: { sendgrid_api_key: sendgridKey, sender_email: senderEmail },
          withCredentials: true 
        }
      );
      toast.success('Configurações de email guardadas!');
      setSendgridKey(''); // Clear the input after saving
      fetchEmailConfig(); // Refresh to show masked key
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Introduza um email para teste');
      return;
    }
    setTesting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/config/email/test`,
        null,
        { 
          params: { test_email: testEmail },
          withCredentials: true 
        }
      );
      toast.success(`Email de teste enviado para ${testEmail}!`);
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email de teste');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-8 dark:bg-slate-900" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] dark:text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('settings.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Gerir as definições da plataforma e da sua conta
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {['admin', 'tenant_admin', 'super_admin', 'dpo'].includes(user?.role) && <TenantBannerCard />}
        {/* User Profile */}
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
          <CardHeader>
            <div className="flex items-center gap-3">
              <User className="w-6 h-6 text-[#3B82F6]" />
              <div>
                <CardTitle className="dark:text-white">Perfil de Utilizador</CardTitle>
                <CardDescription className="mt-1 dark:text-slate-400">
                  Informação da sua conta
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="dark:text-slate-200">Nome</Label>
              <Input value={user?.name || ''} disabled className="mt-2 dark:bg-slate-700 dark:text-white" />
            </div>
            <div>
              <Label className="dark:text-slate-200">Email</Label>
              <Input value={user?.email || ''} disabled className="mt-2 dark:bg-slate-700 dark:text-white" />
            </div>
            <div>
              <Label className="dark:text-slate-200">Função</Label>
              <div className="mt-2">
                <span className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-1 text-sm font-semibold text-slate-700 dark:text-slate-200 uppercase">
                  {user?.role}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Language & Theme Settings */}
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Palette className="w-6 h-6 text-[#8B5CF6]" />
              <div>
                <CardTitle className="dark:text-white">{t('settings.language')} & {t('settings.theme')}</CardTitle>
                <CardDescription className="mt-1 dark:text-slate-400">
                  Personalizar a interface
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Language Selection */}
            <div>
              <Label className="flex items-center gap-2 mb-3 dark:text-slate-200">
                <Globe className="w-4 h-4" />
                {t('settings.language')}
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="dark:bg-slate-700 dark:text-white dark:border-slate-600" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Theme Selection */}
            <div>
              <Label className="flex items-center gap-2 mb-3 dark:text-slate-200">
                {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                {t('settings.theme')}
              </Label>
              <div className="flex gap-2">
                <Button
                  variant={theme === 'light' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('light')}
                  className="flex-1"
                  data-testid="theme-light"
                >
                  <Sun className="w-4 h-4 mr-2" />
                  {t('settings.lightTheme')}
                </Button>
                <Button
                  variant={theme === 'dark' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('dark')}
                  className="flex-1"
                  data-testid="theme-dark"
                >
                  <Moon className="w-4 h-4 mr-2" />
                  {t('settings.darkTheme')}
                </Button>
              </div>
            </div>

            {/* Quick Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <div className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <span className="text-sm dark:text-slate-200">Modo Escuro</span>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Email Configuration (Operador/DPO only) */}
        {(user?.role === 'operador' || user?.role === 'dpo' || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'tenant_admin') && (
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-6 h-6 text-[#10B981]" />
                  <div>
                    <CardTitle className="dark:text-white">Configuração de Email</CardTitle>
                    <CardDescription className="mt-1 dark:text-slate-400">
                      SendGrid API para envio de emails e alertas
                    </CardDescription>
                  </div>
                </div>
                {emailConfigured ? (
                  <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                    <Check className="w-3 h-3 mr-1" />Configurado
                  </Badge>
                ) : (
                  <Badge className="bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                    Não Configurado
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sendgrid-key" className="dark:text-slate-200">SendGrid API Key</Label>
                      <Input
                        id="sendgrid-key"
                        data-testid="sendgrid-key-input"
                        type="password"
                        value={sendgridKey}
                        onChange={(e) => setSendgridKey(e.target.value)}
                        placeholder={maskedKey || "SG.xxxxx..."}
                        className="mt-2 dark:bg-slate-700 dark:text-white"
                      />
                      {maskedKey && !sendgridKey && (
                        <p className="text-xs text-slate-500 mt-1">Chave atual: {maskedKey}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        Obtenha a sua API Key em <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">sendgrid.com</a>
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="sender-email" className="dark:text-slate-200">Email de Envio</Label>
                      <Input
                        id="sender-email"
                        data-testid="sender-email-input"
                        type="email"
                        value={senderEmail}
                        onChange={(e) => setSenderEmail(e.target.value)}
                        placeholder="noreply@privaxia.eu"
                        className="mt-2 dark:bg-slate-700 dark:text-white"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Email verificado no SendGrid como remetente
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    data-testid="save-email-settings-button"
                    onClick={handleSaveEmailSettings}
                    disabled={saving}
                    className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    {t('common.save')} Configurações
                  </Button>

                  {emailConfigured && (
                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                      <Label className="dark:text-slate-200">Testar Configuração</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          type="email"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          placeholder="email@exemplo.com"
                          className="flex-1 dark:bg-slate-700 dark:text-white"
                        />
                        <Button
                          onClick={handleTestEmail}
                          disabled={testing || !testEmail}
                          variant="outline"
                        >
                          {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Enviar Teste
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Envie um email de teste para verificar se a configuração está correta
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Consent Purposes Settings */}
      {(user?.role === 'admin' || user?.role === 'dpo' || user?.role === 'super_admin' || user?.role === 'tenant_admin') && (
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Target className="w-6 h-6 text-[#8B5CF6]" />
                <div>
                  <CardTitle className="dark:text-white">Finalidades de Consentimento</CardTitle>
                  <CardDescription className="mt-1 dark:text-slate-400">
                    Gerir as finalidades disponíveis para recolha de consentimento
                  </CardDescription>
                </div>
              </div>
              <Button 
                onClick={() => handleOpenPurposeDialog()} 
                className="bg-[#10B981] hover:bg-[#10B981]/90"
                data-testid="add-purpose-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Finalidade
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Finalidade</TableHead>
                  <TableHead>Base Legal</TableHead>
                  <TableHead>Prazo Conservação</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purposes.map((purpose) => (
                  <TableRow key={purpose.purpose_id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{purpose.name}</span>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{purpose.description}</p>
                        <code className="text-xs text-slate-400">{purpose.purpose_id}</code>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-slate-400" />
                        <span className="text-sm">{getLegalBasisLabel(purpose.legal_basis)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span>{purpose.retention_period_label || `${purpose.retention_period_days} dias`}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={purpose.is_active ? "default" : "secondary"} className={purpose.is_active ? "bg-emerald-500" : ""}>
                        {purpose.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenPurposeDialog(purpose)}
                          data-testid={`edit-purpose-${purpose.purpose_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePurpose(purpose.purpose_id)}
                          className="text-red-500 hover:text-red-600"
                          data-testid={`delete-purpose-${purpose.purpose_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {purposes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                      Nenhuma finalidade configurada. Clique em "Nova Finalidade" para adicionar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Purpose Create/Edit Dialog */}
      <Dialog open={purposeDialog.open} onOpenChange={(open) => setPurposeDialog({ open, purpose: open ? purposeDialog.purpose : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#8B5CF6]" />
              {purposeDialog.purpose ? 'Editar Finalidade' : 'Nova Finalidade de Consentimento'}
            </DialogTitle>
            <DialogDescription>
              Configure os detalhes da finalidade de consentimento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="purpose-id">ID da Finalidade *</Label>
                <Input
                  id="purpose-id"
                  value={purposeForm.purpose_id}
                  onChange={(e) => setPurposeForm({ ...purposeForm, purpose_id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="newsletter"
                  disabled={!!purposeDialog.purpose}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Identificador único (sem espaços)</p>
              </div>
              <div>
                <Label htmlFor="purpose-name">Nome *</Label>
                <Input
                  id="purpose-name"
                  value={purposeForm.name}
                  onChange={(e) => setPurposeForm({ ...purposeForm, name: e.target.value })}
                  placeholder="Newsletter"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="purpose-description">Descrição</Label>
              <Input
                id="purpose-description"
                value={purposeForm.description}
                onChange={(e) => setPurposeForm({ ...purposeForm, description: e.target.value })}
                placeholder="Receber comunicações periódicas..."
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Base Legal *</Label>
                <Select value={purposeForm.legal_basis} onValueChange={(value) => setPurposeForm({ ...purposeForm, legal_basis: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {legalBasisOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prazo de Conservação *</Label>
                <Select value={purposeForm.retention_period_days.toString()} onValueChange={handleRetentionChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {retentionOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label>Estado Ativo</Label>
                  <p className="text-xs text-slate-500">Disponível para seleção</p>
                </div>
                <Switch
                  checked={purposeForm.is_active}
                  onCheckedChange={(checked) => setPurposeForm({ ...purposeForm, is_active: checked })}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label>Consentimento Explícito</Label>
                  <p className="text-xs text-slate-500">Requer opt-in</p>
                </div>
                <Switch
                  checked={purposeForm.requires_explicit_consent}
                  onCheckedChange={(checked) => setPurposeForm({ ...purposeForm, requires_explicit_consent: checked })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurposeDialog({ open: false, purpose: null })}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSavePurpose} 
              disabled={savingPurpose}
              className="bg-[#10B981] hover:bg-[#10B981]/90"
            >
              {savingPurpose ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {purposeDialog.purpose ? 'Guardar Alterações' : 'Criar Finalidade'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GDPR Compliance Info */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#10B981]" />
            <div>
              <CardTitle className="dark:text-white">Conformidade RGPD</CardTitle>
              <CardDescription className="mt-1 dark:text-slate-400">
                Informação sobre proteção de dados
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <h4 className="font-semibold text-[#0F172A] dark:text-emerald-200 mb-2">Medidas de Segurança Implementadas:</h4>
              <ul className="text-sm text-slate-600 dark:text-emerald-300 space-y-1">
                <li>• Encriptação de dados em repouso e em trânsito</li>
                <li>• Autenticação via Google OAuth (Emergent)</li>
                <li>• Controlo de acessos baseado em funções (RBAC)</li>
                <li>• Logs de auditoria imutáveis</li>
                <li>• Validação de consentimentos via OTP e QR Code</li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-semibold text-[#0F172A] dark:text-blue-200 mb-2">Contactos:</h4>
              <div className="text-sm text-slate-600 dark:text-blue-300 space-y-1">
                <p><span className="font-medium">Email:</span> info@privaxia.eu</p>
                <p><span className="font-medium">Telefone:</span> +351 252 00 44 77</p>
                <p><span className="font-medium">CNPD:</span> www.cnpd.pt</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
